import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import Quill from 'quill';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import {
  applyDynamicValidators,
  buildEnglishTranslationState,
  buildLocalTranslatedState,
  buildManualTranslatedPayload,
  buildVisibleFields,
  cloneTranslatedState,
  ComponentInfoFormValue,
  ComponentTranslationInfoValue,
  ComponentTranslationValue,
  createComponentForm,
  createEmptyTranslatedState,
  createInfoFieldGroup,
  ensureTranslatedInfoFieldCount,
  hasMeaningfulText,
  hasTranslatedValueContent,
  isOptionalHttpUrlValid,
  isYouTubeUrl,
  mapApiInfoToFormSeed,
  sanitizeString,
  serializeInfoFields,
  shouldSendManualTranslatedContent,
  VisibleFieldKey,
} from '../shared/component-form.helpers';

declare let $: any;

@Component({
  selector: 'app-edit-component',
  templateUrl: './edit-component.component.html',
  styleUrls: ['./edit-component.component.scss'],
})
export class EditComponentComponent implements OnInit {
  @Input() componentId!: number;
  @Output() close = new EventEmitter<void>();

  componentForm: FormGroup;
  componentTypes: any[] = [];
  groupedDesigns: Record<number, any[]> = {};
  categories: any[] = [];
  collections: any[] = [];
  subCategories: any[] = [];
  subSubCategories: any[] = [];
  clientSubscriptions: any[] = [];
  componentMeta: any = {};

  loading = false;
  selectedParentId = 0;
  selectedDesign: any = null;
  selectedDesignDescription = '';
  selectedDesignImageUrl = '';
  selectedOption = '';
  productComponentType = 0;
  minItems = 0;
  maxItems = 0;
  errorMessage = '';
  itemListId = '';
  deletedInfoIds: number[] = [];
  urlsToDelete: string[] = [];

  activeSubscriptions = 0;
  selectedSubscriptions: number[] = [];

  currentStep = 1;
  selectedIndex = 0;
  selectedLanguage: 'es' | 'en' = 'es';
  submitAttempted = false;
  navigationAttempted = false;
  visibleFields = new Set<VisibleFieldKey>();
  manualTranslationEnabled = false;
  hasExistingEnglishTranslation = false;
  hasPersistedManualTranslation = false;
  englishTranslation: ComponentTranslationValue = createEmptyTranslatedState();
  initialEnglishTranslation: ComponentTranslationValue = createEmptyTranslatedState();

  readonly maxLength = 2500;
  readonly infoMaxLength = 2500;
  remainingCharacters = this.maxLength;
  infoCharacterCounts: number[] = [];

  readonly steps = [
    { index: 1, label: 'COMPONENT_STEP_1_LABEL', description: 'COMPONENT_STEP_1_DESCRIPTION' },
    { index: 2, label: 'COMPONENT_STEP_2_LABEL', description: 'COMPONENT_STEP_2_DESCRIPTION' },
    { index: 3, label: 'COMPONENT_STEP_3_LABEL', description: 'COMPONENT_STEP_3_DESCRIPTION' },
    { index: 4, label: 'COMPONENT_STEP_4_LABEL', description: 'COMPONENT_STEP_4_DESCRIPTION' },
  ];

  parentIdNames: { [key: number]: string } = { 1: 'BANNERS', 2: 'CARDS', 3: 'PRODUCTS' };
  modules = {
    'emoji-shortname': true,
    'emoji-textarea': true,
    'emoji-toolbar': true,
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['link'],
      ['emoji'],
      ['clean'],
      [{ font: ['arial', 'inter', 'lato', 'merriweather', 'montserrat', 'nunito', 'pacifico', 'poppins', 'raleway', 'roboto', 'serif'] }],
    ],
  };

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private themingService: ThemingService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {
    this.componentForm = createComponentForm(this.fb);
  }

  get componentGroup(): FormGroup { return this.componentForm.get('component') as FormGroup; }
  get infoFields(): FormArray { return this.componentForm.get('infoFields') as FormArray; }
  get selectedInfoField(): FormGroup | null { return (this.infoFields.at(this.selectedIndex) as FormGroup) || null; }
  get hasSelectedInfoField(): boolean { return this.selectedIndex >= 0 && this.selectedIndex < this.infoFields.length; }
  get activeInfoField(): FormGroup { return this.infoFields.at(this.selectedIndex) as FormGroup; }
  get layoutOptions(): any[] {
    const componentTypeId = Number(this.componentGroup.get('componentTypeId')?.value || this.selectedDesign?.typeId || 0);
    const groupedOptions = this.groupedDesigns[this.selectedParentId] || [];
    const selectedOption = groupedOptions.find((design) => Number(design.typeId || 0) === componentTypeId);

    if (selectedOption) {
      return [selectedOption];
    }

    return this.selectedDesign ? [this.selectedDesign] : [];
  }
  get stepProgress(): number { return (this.currentStep / this.steps.length) * 100; }
  get usesInfoTabs(): boolean { return !!this.selectedDesign && this.selectedParentId !== 3; }
  get currentInfoRemainingCharacters(): number { return this.infoMaxLength - (this.infoCharacterCounts[this.selectedIndex] || 0); }
  get hasEnglishPreview(): boolean { return this.manualTranslationEnabled || this.hasExistingEnglishTranslation || this.hasPersistedManualTranslation; }
  get isEnglishReadonly(): boolean { return !this.manualTranslationEnabled; }

  ngOnInit(): void {
    this.loading = true;
    Promise.all([
      this.getComponentById(this.componentId),
      this.getComponentTypes(),
      this.getCollections(),
      this.getCategories(),
      this.getSubCategories(),
      this.getSubSubCategories(),
      this.getClientSubscriptions(),
      Promise.resolve(this.registerFonts()),
    ]).then(() => this.loading = false).catch((error) => {
      console.error(error);
      this.loading = false;
    });
  }

  registerFonts(): void {
    const FontAttributor = Quill.import('attributors/class/font');
    FontAttributor.whitelist = ['arial', 'inter', 'lato', 'merriweather', 'montserrat', 'nunito', 'pacifico', 'poppins', 'raleway', 'roboto', 'serif'];
    Quill.register(FontAttributor, true);
  }

  async getComponentById(id: number): Promise<void> {
    const data: any = await this.lambdaService.getComponentById(id, this.themingService.shopId);
    this.componentMeta = data;
    this.selectedParentId = Number(data.componentParentId || 0);
    this.productComponentType = Number(data.productComponentType || 0);
    this.minItems = Number(data.minItems || 0);
    this.maxItems = Number(data.maxItems || 0);
    this.selectedDesign = {
      name: data.componentTypeName || '',
      typeId: Number(data.componentTypeId || 0),
      description: data.componentTypeDescription || '',
      imageUrl: data.imageUrl || '',
    };
    this.selectedDesignDescription = this.selectedDesign.description;
    this.selectedDesignImageUrl = this.selectedDesign.imageUrl;

    this.componentForm = createComponentForm(this.fb);
    this.componentGroup.patchValue({
      componentId: Number(data.componentId || 0),
      componentName: data.componentName || '',
      title: data.title || '',
      subtitle: data.subtitle || '',
      description: data.description || '',
      shopContactMail: data.shopContactMail || '',
      componentTypeId: Number(data.componentTypeId || 0),
      status: Number(data.status || 0),
      position: Number(data.position || 0),
      itemQuantity: Number(data.itemQuantity || 0),
      itemListId: Number(data.itemListId || 0),
      itemSort: Number(data.itemSort || 0),
      categoryId: Number(data.categoryId || 0),
      subCategoryId: Number(data.subCategoryId || 0),
      subSubCategoryId: Number(data.subSubCategoryId || 0),
      collectionId: Number(data.collectionId || 0),
      pageId: Number(data.pageId || 0),
    });

    this.setSelectedOptionFromData(data);
    this.setSubscriptionsState(data.clientSubscriptionsAllowed);
    this.remainingCharacters = this.maxLength - this.getPlainTextLength(data.description);
    this.updateVisibleFields();
    this.infoFields.clear();
    this.infoCharacterCounts = [];
    this.deletedInfoIds = [];
    this.urlsToDelete = [];

    (Array.isArray(data.componentInfo) ? data.componentInfo : []).forEach((info: any) => {
      const seed = mapApiInfoToFormSeed(info);
      this.infoFields.push(createInfoFieldGroup(this.fb, this.visibleFields, seed));
      this.infoCharacterCounts.push(this.getPlainTextLength(seed.infoDescription || ''));
    });

    this.selectedIndex = 0;
    applyDynamicValidators(this.componentGroup, this.infoFields, this.visibleFields);
    this.initializeTranslationState(data.translatedContent);
  }

  async getComponentTypes(): Promise<void> {
    const response: any = await this.lambdaService.getComponentTypes(this.themingService.shopId);
    this.componentTypes = response.data;
    this.groupedDesigns = this.componentTypes.reduce((acc, curr) => {
      if (!acc[curr.componentParentId]) acc[curr.componentParentId] = [];
      acc[curr.componentParentId].push({
        id: curr.componentTypeDesignId,
        name: curr.componentTypeName,
        typeId: curr.componentTypeId,
        description: curr.componentTypeDescription,
        imageUrl: curr.imageUrl,
      });
      return acc;
    }, {} as Record<number, any[]>);
  }

  async getClientSubscriptions(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getClientSubscriptions(this.themingService.shopId);
      this.clientSubscriptions = Array.isArray(response.subscriptions) ? response.subscriptions : [];
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      this.clientSubscriptions = [];
    }
  }

  async getCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);
      this.categories = Array.isArray(response[0]) ? response[0] : [];
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async getCollections(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCollections(this.themingService.shopId);
      this.collections = Array.isArray(response.collections) ? response.collections : [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      this.collections = [];
    }
  }

  async getSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubCategories(this.themingService.shopId);
      this.subCategories = Array.isArray(response.subCategories) ? response.subCategories : [];
    } catch (error) {
      console.error('Error fetching subCategories:', error);
    }
  }

  async getSubSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubSubCategories(this.themingService.shopId);
      this.subSubCategories = Array.isArray(response.subSubCategories) ? response.subSubCategories : [];
    } catch (error) {
      console.error('Error fetching subSubCategories:', error);
    }
  }

  onOptionChange(option: string): void {
    this.selectedOption = option;
    this.componentGroup.patchValue({ categoryId: 0, subCategoryId: 0, subSubCategoryId: 0, collectionId: 0 });
    this.productComponentType = option === 'categories' ? 1 : option === 'subCategories' ? 2 : option === 'subSubcategories' ? 3 : option === 'collections' ? 4 : Number(this.componentMeta?.productComponentType || 0);
  }

  onCategoryChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({ categoryId: value, subCategoryId: 0, subSubCategoryId: 0, collectionId: 0 });
  }

  onSubCategoryChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({ categoryId: 0, subCategoryId: value, subSubCategoryId: 0, collectionId: 0 });
  }

  onSubSubCategoryChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({ categoryId: 0, subCategoryId: 0, subSubCategoryId: value, collectionId: 0 });
  }

  onCollectionChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({ categoryId: 0, subCategoryId: 0, subSubCategoryId: 0, collectionId: value });
  }

  onItemQuantityChange(event: Event): void {
    this.componentGroup.get('itemQuantity')?.setValue(Number((event.target as HTMLSelectElement).value || 0));
  }

  changeComponentTypeId(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  updateVisibleFields(): void {
    this.visibleFields = buildVisibleFields(Number(this.componentGroup.get('componentTypeId')?.value || 0));
    applyDynamicValidators(this.componentGroup, this.infoFields, this.visibleFields);
  }

  addNewField(): void {
    if (this.infoFields.length >= this.maxItems) {
      this.errorMessage = this.translate.instant('COMPONENT_MAX_ITEMS_ERROR', { count: this.maxItems });
      $('#errorEditMaxMinItemModal').modal('show');
      return;
    }
    this.infoFields.push(createInfoFieldGroup(this.fb, this.visibleFields, { infoId: this.generateNewInfoId() }));
    this.infoCharacterCounts.push(0);
    this.selectedIndex = this.infoFields.length - 1;
    applyDynamicValidators(this.componentGroup, this.infoFields, this.visibleFields);
    this.syncTranslationInfoFieldState();
    this.focusItemPanel(this.selectedIndex);
  }

  removeField(index: number): void {
    if (this.infoFields.length <= this.minItems) {
      this.errorMessage = this.translate.instant('COMPONENT_MIN_ITEMS_ERROR', { count: this.minItems });
      $('#errorEditMaxMinItemModal').modal('show');
      return;
    }
    const infoId = Number((this.infoFields.at(index) as FormGroup).get('infoId')?.value || 0);
    if (infoId > 0) this.deletedInfoIds.push(infoId);
    this.infoFields.removeAt(index);
    this.infoCharacterCounts.splice(index, 1);
    this.selectedIndex = Math.max(0, Math.min(this.selectedIndex, this.infoFields.length - 1));
    this.syncTranslationInfoFieldState();
  }

  selectItem(index: number): void { this.selectedIndex = index; }
  selectLanguage(language: 'es' | 'en'): void {
    this.selectedLanguage = language;
    if (language === 'en') {
      this.syncTranslationInfoFieldState();
    }
  }

  nextStep(): void {
    this.navigationAttempted = true;
    if (!this.isStepValid(this.currentStep)) {
      this.markStepAsTouched(this.currentStep);
      return;
    }
    this.currentStep = Math.min(this.currentStep + 1, this.steps.length);
    this.navigationAttempted = false;
  }

  prevStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 1);
    this.navigationAttempted = false;
  }

  goToStep(target: number): void {
    if (target === this.currentStep) {
      return;
    }

    if (target < this.currentStep) {
      this.currentStep = target;
      this.navigationAttempted = false;
      return;
    }

    for (let step = this.currentStep; step < target; step += 1) {
      if (!this.isStepValid(step)) {
        this.currentStep = step;
        this.navigationAttempted = true;
        this.markStepAsTouched(step);
        return;
      }
    }

    this.currentStep = target;
    this.navigationAttempted = false;
  }

  async validateFormData(): Promise<void> {
    this.submitAttempted = true;
    for (let step = 1; step <= this.steps.length; step += 1) {
      if (!this.isStepValid(step)) {
        this.currentStep = step;
        this.markStepAsTouched(step);
        return;
      }
    }

    this.loading = true;
    try {
      await this.updateComponentFiles();
      await this.deletePreviousFiles();
      const component = this.componentGroup.getRawValue();
      const infoFields = this.infoFields.getRawValue() as ComponentInfoFormValue[];
      const serialized = serializeInfoFields(infoFields);
      const translatedContent = this.resolveTranslatedContentPayload();

      await this.lambdaService.updateComponent(
        component.componentId,
        this.themingService.shopId,
        sanitizeString(component.title),
        sanitizeString(component.subtitle),
        sanitizeString(component.description),
        sanitizeString(component.shopContactMail),
        Number(component.itemQuantity || 0),
        component.itemListId,
        component.itemSort,
        serialized.imageUrls,
        serialized.descriptions,
        serialized.redirectUrls,
        infoFields.map((info) => String(info.infoId || 0)).join('|'),
        this.deletedInfoIds.join('|'),
        serialized.infoTitles,
        serialized.urlNames,
        Number(component.collectionId || 0),
        Number(component.categoryId || 0),
        Number(component.subCategoryId || 0),
        Number(component.subSubCategoryId || 0),
        this.productComponentType || Number(this.componentMeta?.productComponentType || 0),
        Number(component.componentTypeId || 0),
        sanitizeString(component.componentName),
        serialized.videoUrls,
        serialized.videoUrlTypes,
        Number(component.pageId || 0),
        Number(component.status || 0),
        this.resolveSubscriptionsAllowed(),
        translatedContent
      );

      this.loading = false;
      $('#successEditComponentModal').modal('show');
      await this.getComponentById(this.componentId);
    } catch (error) {
      console.error(error);
      this.loading = false;
      $('#errorEditComponentModal').modal('show');
    }
  }

  descriptionChanged(event: any): void {
    if (event.event !== 'text-change') {
      return;
    }


    const quill = event.editor;
    const textLength = Math.max(quill.getLength() - 1, 0);
    this.remainingCharacters = this.maxLength - textLength;
    if (textLength > this.maxLength) {
      quill.deleteText(this.maxLength, textLength - this.maxLength);
      this.remainingCharacters = 0;
    }
  }

  infoDescriptionChanged(event: any, index: number): void {
    if (event.event !== 'text-change') {
      return;
    }

    const quill = event.editor;
    const textLength = Math.max(quill.getLength() - 1, 0);
    this.infoCharacterCounts[index] = textLength;
    if (textLength > this.infoMaxLength) {
      quill.deleteText(this.infoMaxLength, textLength - this.infoMaxLength);
      this.infoCharacterCounts[index] = this.infoMaxLength;
    }
  }

  onManualTranslationToggle(event: Event): void {
    const enabled = (event.target as HTMLInputElement).checked;
    this.manualTranslationEnabled = enabled;

    if (enabled && !this.hasExistingEnglishTranslation && !this.hasPersistedManualTranslation && !hasTranslatedValueContent(this.englishTranslation)) {
      const fallback = this.buildCurrentEnglishFallback();
      this.englishTranslation = fallback;
      this.initialEnglishTranslation = cloneTranslatedState(fallback);
      this.syncTranslationInfoFieldState();
    }

    if (enabled) {
      this.selectedLanguage = 'en';
    }
  }

  updateEnglishField(fieldName: 'title' | 'subtitle' | 'description', value: string): void {
    this.englishTranslation = {
      ...this.englishTranslation,
      [fieldName]: value || '',
    };
  }

  updateEnglishInfoField(index: number, fieldName: keyof ComponentTranslationInfoValue, value: string): void {
    this.syncTranslationInfoFieldState();
    this.englishTranslation.infoFields[index] = {
      ...this.englishTranslation.infoFields[index],
      [fieldName]: value || '',
    };
  }

  toggle(): void {
    this.activeSubscriptions = this.activeSubscriptions === 1 ? 0 : 1;
    if (this.activeSubscriptions === 0) this.selectedSubscriptions = [];
  }

  toggleSuscripcion(id: number): void {
    const index = this.selectedSubscriptions.indexOf(id);
    if (index > -1) this.selectedSubscriptions.splice(index, 1);
    else this.selectedSubscriptions.push(id);
  }

  triggerFileInput(index: number, type: 'image' | 'video'): void {
    const input = document.getElementById(type === 'image' ? `btn-upload-image-${index}` : `btn-upload-video-${index}`) as HTMLInputElement;
    input?.click();
  }

  async handleFileSelection(index: number, event: Event, type: 'image' | 'video'): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      $('#errorFileSizeModal').modal('show');
      (event.target as HTMLInputElement).value = '';
      return;
    }

    const fieldGroup = this.infoFields.at(index) as FormGroup;
    if (type === 'image' && this.isRemoteMedia(fieldGroup.get('imageURL')?.value)) this.urlsToDelete.push(fieldGroup.get('imageURL')?.value);
    if (type === 'video' && this.isRemoteMedia(fieldGroup.get('videoURL')?.value)) this.urlsToDelete.push(fieldGroup.get('videoURL')?.value);

    if (type === 'image') {
      try {
        const webpFile = await this.convertImageToWebP(file, 1920, 0.8);
        fieldGroup.patchValue({
          imageURL: URL.createObjectURL(webpFile),
          blob: { file: webpFile, key: `${this.themingService.shopId}/${environment.stage}/images/components/component_${Date.now()}.webp` },
        });
      } catch (error) {
        console.error('Error al convertir a WebP:', error);
        const extension = file.name.split('.').pop();
        fieldGroup.patchValue({
          imageURL: URL.createObjectURL(file),
          blob: { file, key: `${this.themingService.shopId}/${environment.stage}/images/components/component_${Date.now()}.${extension}` },
        });
      }
    } else {
      const extension = file.name.split('.').pop();
      fieldGroup.patchValue({
        videoURL: URL.createObjectURL(file),
        videoBlob: { file, key: `${this.themingService.shopId}/${environment.stage}/videos/components/component_${Date.now()}.${extension}` },
        videoManualURL: '',
        videoUrlType: 1,
        isYouTube: false,
      });
    }
  }

  onVideoSourceChange(index: number, event: Event): void {
    const selectedValue = Number((event.target as HTMLSelectElement).value || 1);
    const fieldGroup = this.infoFields.at(index) as FormGroup;
    const currentVideoUrl = fieldGroup.get('videoURL')?.value || '';
    const currentManualUrl = fieldGroup.get('videoManualURL')?.value || '';
    if (selectedValue === 1) {
      fieldGroup.patchValue({ videoUrlType: 1, previousManualUrl: currentManualUrl, videoManualURL: '', isYouTube: false });
      return;
    }
    fieldGroup.patchValue({ videoUrlType: 2, previousVideoUrl: currentVideoUrl, videoURL: '', videoBlob: null });
  }

  handleVideoManualURLChange(index: number, event: Event): void {
    const value = sanitizeString((event.target as HTMLInputElement).value);
    const fieldGroup = this.infoFields.at(index) as FormGroup;
    fieldGroup.patchValue({ videoManualURL: value, videoURL: '', videoBlob: null, isYouTube: isYouTubeUrl(value) });
  }

  removeMedia(index: number, field: 'imageURL' | 'videoURL'): void {
    const fieldGroup = this.infoFields.at(index) as FormGroup;
    if (field === 'imageURL') {
      const currentUrl = fieldGroup.get('imageURL')?.value;
      if (this.isRemoteMedia(currentUrl)) this.urlsToDelete.push(currentUrl);
      fieldGroup.patchValue({ imageURL: '', blob: null });
      return;
    }
    const currentVideoUrl = fieldGroup.get('videoURL')?.value || fieldGroup.get('videoManualURL')?.value;
    if (this.isRemoteMedia(currentVideoUrl)) this.urlsToDelete.push(currentVideoUrl);
    fieldGroup.patchValue({ videoURL: '', videoBlob: null, videoManualURL: '', isYouTube: false });
  }

  sanitizeURL(mediaURL: string): any {
    if (!mediaURL) return '';
    if (mediaURL.match(/\.(jpeg|jpg|gif|png|webp)$/i)) return this.sanitizer.bypassSecurityTrustUrl(mediaURL);
    return this.sanitizer.bypassSecurityTrustResourceUrl(mediaURL);
  }

  sanitizeVideoURL(videoURL: string): SafeUrl {
    if (this.isYouTubeVideo(videoURL)) return this.sanitizer.bypassSecurityTrustResourceUrl(videoURL.replace('watch?v=', 'embed/'));
    return this.sanitizer.bypassSecurityTrustResourceUrl(videoURL);
  }

  isYouTubeVideo(url: string): boolean { return isYouTubeUrl(url); }
  captureThumbnail(video: HTMLVideoElement): void { video.currentTime = 0; video.pause(); }
  trackByLayoutOption(_: number, design: { typeId: number }): number { return Number(design.typeId || 0); }
  trackByIndex(index: number): number { return index; }
  isCurrentStep(step: number): boolean { return this.currentStep === step; }
  isStepComplete(step: number): boolean { return step < this.currentStep && this.isStepValid(step); }

  isStepValid(step: number): boolean {
    switch (step) {
      case 1: return this.validateStepOne();
      case 2: return this.validateStepTwo();
      case 3: return this.validateStepThree();
      case 4: return this.validateStepFour();
      default: return true;
    }
  }

  getTabState(index: number): 'valid' | 'incomplete' | 'error' {
    const group = this.infoFields.at(index) as FormGroup;
    if (!group) return 'incomplete';
    if (this.hasManualTranslationErrors(index) && (this.submitAttempted || (this.navigationAttempted && this.currentStep === 4))) return 'error';
    if (group.invalid && (group.touched || this.submitAttempted || (this.navigationAttempted && this.currentStep === 4))) return 'error';
    return this.isInfoGroupComplete(group, index) ? 'valid' : 'incomplete';
  }

  shouldShowControlError(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.touched || this.submitAttempted || this.navigationAttempted);
  }

  shouldShowTranslationFieldError(fieldName: 'title' | 'subtitle' | 'description'): boolean {
    if (!this.manualTranslationEnabled || !this.visibleFields.has(fieldName as VisibleFieldKey)) return false;
    return !hasMeaningfulText(this.englishTranslation[fieldName]) && (this.submitAttempted || this.navigationAttempted);
  }

  shouldShowTranslationInfoError(index: number, fieldName: keyof ComponentTranslationInfoValue): boolean {
    if (!this.manualTranslationEnabled) return false;
    const fieldValue = this.getEnglishInfoField(index)?.[fieldName] || '';
    const shouldValidate =
      fieldName === 'infoTitle'
        ? this.visibleFields.has('infoTitle')
        : fieldName === 'infoDescription'
          ? this.visibleFields.has('infoDescription')
          : fieldName === 'redirectUrl'
            ? this.visibleFields.has('redirectUrl')
            : this.visibleFields.has('urlName');

    if (!shouldValidate) return false;
    if (fieldName === 'redirectUrl') return !isOptionalHttpUrlValid(fieldValue) && (this.submitAttempted || (this.navigationAttempted && this.currentStep === 4));
    return !hasMeaningfulText(fieldValue) && (this.submitAttempted || (this.navigationAttempted && this.currentStep === 4));
  }

  getEnglishInfoField(index: number): ComponentTranslationInfoValue {
    return this.englishTranslation.infoFields[index];
  }

  getInfoRemainingCharacters(index: number): number {
    return this.infoMaxLength - (this.infoCharacterCounts[index] || 0);
  }

  closeModal(): void {
    $('#successEditComponentModal').modal('hide');
    $('#errorEditComponentModal').modal('hide');
    $('#errorEditMaxMinItemModal').modal('hide');
    $('#errorFileSizeModal').modal('hide');
    $('#errorEditRedirectUrl').modal('hide');
  }

  closeComponent(): void { this.close.emit(); }

  private validateStepOne(): boolean {
    if (this.componentGroup.get('componentName')?.invalid || !this.selectedParentId) return false;
    if (this.selectedParentId !== 3) return true;
    if (!this.selectedOption) return false;
    const component = this.componentGroup.getRawValue();
    switch (this.selectedOption) {
      case 'collections': return Number(component.collectionId || 0) > 0;
      case 'categories': return Number(component.categoryId || 0) > 0;
      case 'subCategories': return Number(component.subCategoryId || 0) > 0;
      case 'subSubcategories': return Number(component.subSubCategoryId || 0) > 0;
      default: return true;
    }
  }

  private validateStepTwo(): boolean {
    if (!this.componentGroup.get('componentTypeId')?.valid) return false;
    if (!this.visibleFields.has('itemQuantity')) return true;
    return this.componentGroup.get('itemQuantity')?.valid ?? false;
  }

  private validateStepThree(): boolean {
    const spanishValid = ['title', 'subtitle', 'description', 'shopContactMail'].every((controlName) =>
      !this.visibleFields.has(controlName as VisibleFieldKey) || (this.componentGroup.get(controlName)?.valid ?? false)
    );

    if (!spanishValid) {
      this.selectedLanguage = 'es';
      return false;
    }

    const englishValid = this.validateManualTranslationStepThree();
    if (!englishValid) {
      this.selectedLanguage = 'en';
    }

    return englishValid;
  }

  private validateStepFour(): boolean {
    if (!this.usesInfoTabs) return true;
    if (this.infoFields.length < this.minItems) return false;
    const firstInvalidIndex = this.infoFields.controls.findIndex((control, index) => control.invalid || this.hasManualTranslationErrors(index));
    if (firstInvalidIndex > -1) {
      this.selectedIndex = firstInvalidIndex;
      this.selectedLanguage = this.hasManualTranslationErrors(firstInvalidIndex) ? 'en' : 'es';
      return false;
    }
    return this.infoFields.valid && this.validateManualTranslationStepFour();
  }

  private markStepAsTouched(step: number): void {
    if (step === 1) {
      this.componentGroup.get('componentName')?.markAsTouched();
      return;
    }
    if (step === 2) {
      this.componentGroup.get('componentTypeId')?.markAsTouched();
      this.componentGroup.get('itemQuantity')?.markAsTouched();
      return;
    }
    if (step === 3) {
      ['title', 'subtitle', 'description', 'shopContactMail'].forEach((controlName) => {
        if (this.visibleFields.has(controlName as VisibleFieldKey)) this.componentGroup.get(controlName)?.markAsTouched();
      });
      return;
    }
    if (step === 4) this.infoFields.controls.forEach((control) => control.markAllAsTouched());
  }

  private isInfoGroupComplete(group: FormGroup, index: number): boolean {
    if (this.visibleFields.has('infoTitle') && !hasMeaningfulText(group.get('infoTitle')?.value)) return false;
    if (this.visibleFields.has('infoDescription') && !hasMeaningfulText(group.get('infoDescription')?.value)) return false;
    if (this.visibleFields.has('urlName') && !sanitizeString(group.get('urlName')?.value)) return false;
    if (this.visibleFields.has('infoImageUrl') && !sanitizeString(group.get('imageURL')?.value)) return false;
    if (group.get('redirectUrl')?.invalid && sanitizeString(group.get('redirectUrl')?.value)) return false;
    return !this.hasManualTranslationErrors(index);
  }

  private hasManualTranslationErrors(index: number): boolean {
    if (!this.manualTranslationEnabled) return false;
    const translatedInfo = this.getEnglishInfoField(index);
    if (this.visibleFields.has('infoTitle') && !hasMeaningfulText(translatedInfo.infoTitle)) return true;
    if (this.visibleFields.has('infoDescription') && !hasMeaningfulText(translatedInfo.infoDescription)) return true;
    if (this.visibleFields.has('urlName') && !hasMeaningfulText(translatedInfo.urlName)) return true;
    if (this.visibleFields.has('redirectUrl') && !isOptionalHttpUrlValid(translatedInfo.redirectUrl)) return true;
    return false;
  }

  private validateManualTranslationStepThree(): boolean {
    if (!this.manualTranslationEnabled) return true;
    if (this.visibleFields.has('title') && !hasMeaningfulText(this.englishTranslation.title)) return false;
    if (this.visibleFields.has('subtitle') && !hasMeaningfulText(this.englishTranslation.subtitle)) return false;
    if (this.visibleFields.has('description') && !hasMeaningfulText(this.englishTranslation.description)) return false;
    return true;
  }

  private validateManualTranslationStepFour(): boolean {
    if (!this.manualTranslationEnabled || !this.usesInfoTabs) return true;
    return !this.infoFields.controls.some((_, index) => this.hasManualTranslationErrors(index));
  }

  private async convertImageToWebP(file: File, maxSize = 1920, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('Canvas ctx null');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => blob ? resolve(new File([blob], file.name.replace(/\.\w+$/, '.webp'), { type: 'image/webp' })) : reject('No blob'), 'image/webp', quality);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  private async updateComponentFiles(): Promise<void> {
    for (const control of this.infoFields.controls) {
      const fieldGroup = control as FormGroup;
      const imageBlob = fieldGroup.get('blob')?.value;
      const videoBlob = fieldGroup.get('videoBlob')?.value;
      const videoType = Number(fieldGroup.get('videoUrlType')?.value || 0);
      if (imageBlob?.file && imageBlob?.key) {
        const uploaded = await this.lambdaService.uploadFile(imageBlob.key, imageBlob.file, { position: 1, type: 'original' });
        fieldGroup.patchValue({ imageURL: uploaded.imageURL, blob: null });
      }
      if (videoType === 1 && videoBlob?.file && videoBlob?.key) {
        const uploaded = await this.lambdaService.uploadFile(videoBlob.key, videoBlob.file, { position: 1, type: 'original' });
        fieldGroup.patchValue({ videoURL: uploaded.imageURL, videoBlob: null });
      }
    }
  }

  private async deletePreviousFiles(): Promise<void> {
    const deletePromises = this.urlsToDelete
      .filter((url) => this.isRemoteMedia(url))
      .map((url) => this.lambdaService.deleteFile(url.replace('https://mobydyg-files.s3.amazonaws.com/', '')));
    if (!deletePromises.length) return;
    try { await Promise.all(deletePromises); } catch (error) { console.error(error); }
    this.urlsToDelete = [];
  }

  private resolveTranslatedContentPayload(): string | undefined {
    if (
      !shouldSendManualTranslatedContent(
        this.englishTranslation,
        this.initialEnglishTranslation,
        this.manualTranslationEnabled,
        this.hasPersistedManualTranslation
      )
    ) {
      return undefined;
    }

    return JSON.stringify(buildManualTranslatedPayload(this.englishTranslation));
  }

  private resolveSubscriptionsAllowed(): string {
    if (this.activeSubscriptions === 0) return '0';
    return this.selectedSubscriptions.length > 0 ? this.selectedSubscriptions.join(',') : '';
  }

  private initializeTranslationState(rawTranslatedContent: unknown): void {
    const resolved = buildEnglishTranslationState(
      rawTranslatedContent,
      this.componentGroup.getRawValue(),
      this.infoFields.getRawValue() as ComponentInfoFormValue[]
    );

    this.englishTranslation = cloneTranslatedState(resolved.value);
    this.initialEnglishTranslation = cloneTranslatedState(resolved.value);
    this.hasExistingEnglishTranslation = resolved.hasExistingTranslation;
    this.hasPersistedManualTranslation = resolved.isManual;
    this.manualTranslationEnabled = resolved.isManual;
    this.syncTranslationInfoFieldState();
  }

  private buildCurrentEnglishFallback(): ComponentTranslationValue {
    return buildLocalTranslatedState(this.componentGroup.getRawValue(), this.infoFields.getRawValue() as ComponentInfoFormValue[]);
  }

  private syncTranslationInfoFieldState(): void {
    const fallbackInfoFields = this.buildCurrentEnglishFallback().infoFields;
    this.englishTranslation = ensureTranslatedInfoFieldCount(this.englishTranslation, fallbackInfoFields);
    this.initialEnglishTranslation = ensureTranslatedInfoFieldCount(this.initialEnglishTranslation, fallbackInfoFields);
  }

  private focusItemPanel(index: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      const panel = document.getElementById(`edit-item-panel-${index}`);
      panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private setSubscriptionsState(rawValue: string): void {
    if (rawValue === '0') {
      this.activeSubscriptions = 0;
      this.selectedSubscriptions = [];
      return;
    }
    this.activeSubscriptions = 1;
    this.selectedSubscriptions = rawValue ? rawValue.split(',').map((id) => Number(id.trim())).filter(Boolean) : [];
  }

  private setSelectedOptionFromData(data: any): void {
    if (this.selectedParentId !== 3) {
      this.selectedOption = '';
      return;
    }
    if (data.collectionId) this.selectedOption = 'collections';
    else if (data.categoryId) this.selectedOption = 'categories';
    else if (data.subCategoryId) this.selectedOption = 'subCategories';
    else if (data.subSubCategoryId) this.selectedOption = 'subSubcategories';
    else this.selectedOption = '';
  }

  private generateNewInfoId(): number { return -1 * Date.now(); }
  private isRemoteMedia(url: string): boolean { return !!url && url.includes('mobydyg-files.s3.amazonaws.com'); }

  private getPlainTextLength(value: string): number {
    if (!value) return 0;
    return String(value).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim().length;
  }
}
